import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';
import useScoringStatus from './useScoringStatus';
import { ScoringChip, ScoringBanner } from './ScoringStatus';
import { TriStateSelect } from './ListingFieldControls';

export default function PropertyManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [buyers, setBuyers] = useState([]);

  // IDX state
  const [idxQuery, setIdxQuery] = useState({ q: '', city: '', min_price: '', max_price: '', min_beds: '' });
  const [idxResults, setIdxResults] = useState([]);
  const [idxSearching, setIdxSearching] = useState(false);
  const [importing, setImporting] = useState({});
  const [idxStatus, setIdxStatus] = useState(null);

  // Manual form
  const emptyForm = {
    address: '', city: '', state: '', zip: '', price: '', beds: '', baths: '',
    sqft: '', lot_size_acres: '', year_built: '', hoa_monthly: '', school_district: '',
    school_rating: '', garage_spaces: '', property_type: 'single_family',
    listing_status: 'active', listing_url: '', mls_number: '',
    // Extended MLS fields.
    // Booleans start as null ("not stated"), NOT false. False is a definite "no" that
    // counts against the listing when scored; null is excluded from scoring. Defaulting
    // these to false would quietly mark every new listing as having no pool, no
    // waterfront, no gated community, and so on.
    pool: null, pool_features: '',
    waterfront: null, waterfront_features: '', waterfront_type: '',
    gated_community: null, senior_community: null, accessibility_features: null,
    stories: '', bathrooms_full: '', bathrooms_half: '',
    dogs_allowed: null, cats_allowed: null, pets_allowed: '',
    furnished: '',
    elementary_school: '', middle_school: '', high_school: '',
    subdivision: '', county: '', community_amenities: '',
    direction_faces: '',
    cooling: '', heating: '', appliances: '',
    original_list_price: '',
    description: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [expandedSections, setExpandedSections] = useState({
    features: false, location: false, waterfront: false,
    lifestyle: false, utilities: false, pricing: false, description: false,
  });
  const toggleSection = (key) => setExpandedSections(s => ({ ...s, [key]: !s[key] }));

  // PDF upload state
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [pdfDragOver, setPdfDragOver] = useState(false);
  const [autoPopulatedFields, setAutoPopulatedFields] = useState(new Set());
  // Tailwind class to tint auto-populated inputs cyan — used on every always-visible field.
  const autoCls = (field) => autoPopulatedFields.has(field)
    ? ' ring-2 ring-[#00D4FF]/50 ring-offset-1 ring-offset-[#1A2744]'
    : '';

  useEffect(() => {
    loadProperties();
    loadBuyers();
    loadIdxStatus();
  }, [statusFilter]);

  // A newly added or MLS-imported listing scores in the background; show that per row and
  // reload the list once the queue drains.
  const { status: scoringStatus, propertyState, failedJob, retry: retryScoring } =
    useScoringStatus({ onSettled: () => loadProperties() });

  const loadProperties = async () => {
    try {
      let url = `${API}/api/properties?limit=200`;
      if (statusFilter) url += `&listing_status=${statusFilter}`;
      const res = await authFetch(url);
      if (res.ok) setProperties(await res.json());
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadBuyers = async () => {
    try {
      const res = await authFetch(`${API}/api/buyers`);
      if (res.ok) setBuyers(await res.json());
    } catch (err) { /* silent */ }
  };

  const loadIdxStatus = async () => {
    try {
      const res = await authFetch(`${API}/api/idx/status`);
      if (res.ok) setIdxStatus(await res.json());
    } catch (err) { /* silent */ }
  };

  // --- IDX Search & Import ---

  const handleIdxSearch = async (e) => {
    e.preventDefault();
    setIdxSearching(true);
    setError('');
    setIdxResults([]);
    try {
      const params = new URLSearchParams();
      if (idxQuery.q) params.append('q', idxQuery.q);
      if (idxQuery.city) params.append('city', idxQuery.city);
      if (idxQuery.min_price) params.append('min_price', idxQuery.min_price);
      if (idxQuery.max_price) params.append('max_price', idxQuery.max_price);
      if (idxQuery.min_beds) params.append('min_beds', idxQuery.min_beds);
      params.append('limit', '25');

      const res = await authFetch(`${API}/api/idx/search?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setIdxResults(data);
        if (data.length === 0) setError('No listings found. Try different search criteria.');
      } else {
        const err = await res.json();
        setError(err.detail || 'Search failed');
      }
    } catch (err) {
      setError('Network error — check IDX configuration');
    } finally {
      setIdxSearching(false);
    }
  };

  const handleImport = async (simplyrets_id, address) => {
    setImporting(prev => ({ ...prev, [simplyrets_id]: true }));
    setError('');
    try {
      const res = await authFetch(`${API}/api/idx/import/${simplyrets_id}`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        const scoreCount = data.auto_scores?.length || 0;
        setSuccess(`Imported "${address}" — auto-scored for ${scoreCount} buyer(s)`);
        // Mark as imported in results
        setIdxResults(prev => prev.map(r =>
          r.simplyrets_id === simplyrets_id ? { ...r, already_imported: true } : r
        ));
        loadProperties();
      } else {
        const err = await res.json();
        setError(err.detail || 'Import failed');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setImporting(prev => ({ ...prev, [simplyrets_id]: false }));
    }
  };

  // --- Manual Entry ---

  const handleCreate = async (e, asDraft = false) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setSuccess('');
    try {
      const body = { ...form };
      // Listing Lab: save this manual entry as a pre-listing draft. Drafts are
      // visible only to the agent + their broker and never reach any buyer.
      if (asDraft) body.is_draft = true;
      ['price', 'beds', 'sqft', 'year_built', 'garage_spaces', 'stories',
       'bathrooms_full', 'bathrooms_half'].forEach(f => {
        if (body[f]) body[f] = parseInt(body[f]);
        else delete body[f];
      });
      ['baths', 'lot_size_acres', 'hoa_monthly', 'school_rating', 'original_list_price'].forEach(f => {
        if (body[f]) body[f] = parseFloat(body[f]);
        else delete body[f];
      });
      // Strip empty optional text fields so the backend stores NULL rather than ""
      [
        'mls_number', 'listing_url',
        'pool_features', 'waterfront_features', 'waterfront_type', 'pets_allowed',
        'elementary_school', 'middle_school',
        'high_school', 'subdivision', 'county', 'cooling', 'heating', 'appliances',
        'community_amenities', 'description', 'direction_faces', 'furnished',
      ].forEach(f => {
        if (body[f] === '' || body[f] == null) delete body[f];
      });
      // Drop any boolean the agent left as "Not stated" so it is stored as NULL
      // (unknown / scored neutrally) rather than as a definite "no".
      ['pool', 'waterfront', 'gated_community', 'senior_community',
       'accessibility_features', 'dogs_allowed', 'cats_allowed'].forEach(f => {
        if (body[f] === null || body[f] === undefined || body[f] === '') delete body[f];
      });

      const res = await authFetch(`${API}/api/properties`, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const data = await res.json();
        setForm(emptyForm);
        setAutoPopulatedFields(new Set());
        if (asDraft) {
          // Jump straight into Listing Lab to analyze demand for the new draft.
          navigate(`/listing-lab/${data.id}`);
          return;
        }
        setSuccess(`Property "${data.address}" added successfully`);
        setActiveTab('list');
        loadProperties();
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to add property');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setCreating(false);
    }
  };

  // --- PDF MLS Listing Upload ---

  const handlePdfFile = (file) => {
    setPdfError('');
    if (!file) { setPdfFile(null); return; }
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setPdfError('Please upload a .pdf file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setPdfError('PDF too large — max 10MB');
      return;
    }
    setPdfFile(file);
  };

  const handlePdfUpload = async () => {
    if (!pdfFile) return;
    setPdfUploading(true);
    setPdfError('');
    try {
      const fd = new FormData();
      fd.append('file', pdfFile);
      const res = await authFetch(`${API}/api/pdf-import/extract`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        setPdfError(data.detail || 'AI extraction failed');
        setPdfUploading(false);
        return;
      }
      // Merge extracted fields into the manual form, then switch to that tab
      const merged = { ...emptyForm };
      const populated = new Set();
      Object.entries(data.fields).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') {
          // Booleans stay boolean; everything else is stringified for input bindings
          merged[k] = (typeof v === 'boolean') ? v : String(v);
          populated.add(k);
        }
      });
      setForm(merged);
      setAutoPopulatedFields(populated);
      setPdfFile(null);
      setActiveTab('manual');
      // Auto-expand any collapsible section that has at least one populated field
      setExpandedSections(s => ({
        features:    s.features    || populated.has('pool') || populated.has('stories'),
        location:    s.location    || populated.has('elementary_school') || populated.has('middle_school') || populated.has('high_school') || populated.has('subdivision') || populated.has('county') || populated.has('gated_community'),
        waterfront:  s.waterfront  || populated.has('waterfront') || populated.has('waterfront_type'),
        lifestyle:   s.lifestyle   || populated.has('pets_allowed') || populated.has('community_amenities'),
        utilities:   s.utilities   || populated.has('cooling') || populated.has('heating') || populated.has('appliances'),
        pricing:     s.pricing     || populated.has('original_list_price'),
        description: s.description || populated.has('description'),
      }));
      setSuccess(`✨ AI extracted ${data.populated_count} of ${data.total_fields} fields from "${data.filename}". Review and save below.`);
    } catch (err) {
      setPdfError('Network error while uploading');
    } finally {
      setPdfUploading(false);
    }
  };

  const handleDelete = async (propId, address) => {
    if (!window.confirm(`Delete "${address}" and all associated grades and scores?`)) return;
    try {
      const res = await authFetch(`${API}/api/properties/${propId}`, { method: 'DELETE' });
      if (res.ok) loadProperties();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const filteredProperties = search
    ? properties.filter(p =>
        p.address?.toLowerCase().includes(search.toLowerCase()) ||
        p.city?.toLowerCase().includes(search.toLowerCase()) ||
        p.mls_number?.toLowerCase().includes(search.toLowerCase()) ||
        p.zip?.includes(search)
      )
    : properties;

  const formatPrice = (price) => price ? `$${Number(price).toLocaleString()}` : 'N/A';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-lg">Loading properties...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">Property Management</h1>
            <p className="text-[#B0BEC5] text-sm mt-1">{properties.length} properties</p>
          </div>
        </div>

        {/* Messages */}
        {success && (
          <div className="bg-green-900/50 border border-green-600 text-green-200 px-4 py-3 rounded-lg mb-4 text-sm">{success}</div>
        )}
        {error && (
          <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
        )}

        {/* Tabs — mobile dropdown, desktop horizontal strip */}
        <div className="md:hidden mb-6">
          <label htmlFor="tab-select" className="sr-only">Select tab</label>
          <select
            id="tab-select"
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="w-full px-4 py-3 bg-[#1A2744] border border-[#546E7A]/40 rounded-lg text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
          >
            <option value="list">My Properties</option>
            <option value="idx">Import from MLS</option>
            <option value="manual">Manual Entry</option>
            <option value="pdf">✨ Upload MLS PDF</option>
          </select>
        </div>
        <div className="hidden md:flex gap-1 mb-6 border-b border-[#546E7A]/30 overflow-x-auto">
          <button onClick={() => setActiveTab('list')}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'list' ? 'text-[#00D4FF] border-[#00D4FF]' : 'text-gray-400 border-transparent hover:text-white'}`}>
            My Properties
          </button>
          <button onClick={() => setActiveTab('idx')}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'idx' ? 'text-[#00D4FF] border-[#00D4FF]' : 'text-gray-400 border-transparent hover:text-white'}`}>
            Import from MLS
          </button>
          <button onClick={() => setActiveTab('manual')}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'manual' ? 'text-[#00D4FF] border-[#00D4FF]' : 'text-gray-400 border-transparent hover:text-white'}`}>
            Manual Entry
          </button>
          <button onClick={() => setActiveTab('pdf')}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${activeTab === 'pdf' ? 'text-[#00D4FF] border-[#00D4FF]' : 'text-gray-400 border-transparent hover:text-white'}`}>
            ✨ Upload MLS PDF
          </button>
        </div>

        <ScoringBanner status={scoringStatus} onRetry={retryScoring} label="listings" />

        {/* TAB: Property List */}
        {activeTab === 'list' && (
          <>
            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 mb-6">
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                className="w-full md:flex-1 px-4 py-2 bg-[#1A2744] border border-[#546E7A]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                placeholder="Search by address, city, MLS #, or ZIP..." />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full md:w-auto px-4 py-2 bg-[#1A2744] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="sold">Sold</option>
              </select>
            </div>

            {filteredProperties.length === 0 ? (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16">
                <p className="text-[#B0BEC5] text-lg mb-2">No properties found</p>
                <p className="text-gray-500 mb-4">Import listings from the MLS or add properties manually.</p>
                <button onClick={() => setActiveTab('idx')}
                  className="px-6 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] transition-colors">
                  Import from MLS
                </button>
              </div>
            ) : (
              <>
                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {filteredProperties.map(prop => (
                    <div key={prop.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4">
                      <div className="flex justify-between items-start mb-2">
                        <a href={`/properties/${prop.id}`} className="text-white font-medium text-sm hover:text-[#00D4FF] min-w-0 break-words">{prop.address}</a>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ml-2 ${
                          prop.listing_status === 'active' ? 'bg-green-900/50 text-green-300' :
                          prop.listing_status === 'pending' ? 'bg-yellow-900/50 text-yellow-300' :
                          'bg-red-900/50 text-red-300'
                        }`}>{prop.listing_status}</span>
                      </div>
                      <div className="text-gray-300 text-sm mb-1">{prop.city}, {prop.state}</div>
                      {prop.mls_number && <div className="text-gray-500 text-xs mb-2">MLS: {prop.mls_number}</div>}
                      {propertyState(prop.id) && (
                        <div className="mb-2">
                          <ScoringChip state={propertyState(prop.id)} failedJob={failedJob('property', prop.id)} onRetry={retryScoring} />
                        </div>
                      )}
                      <div className="text-[#00E676] font-bold text-lg mb-2">{formatPrice(prop.price)}</div>
                      <div className="flex gap-3 text-gray-400 text-xs mb-3">
                        <span>{prop.beds || '-'} bd</span>
                        <span>{prop.baths || '-'} ba</span>
                        <span>{prop.sqft ? prop.sqft.toLocaleString() : '-'} sqft</span>
                        <span className={`px-2 py-0.5 rounded font-medium ${
                          prop.data_source === 'idx' ? 'bg-cyan-900/50 text-cyan-300' : 'bg-gray-700 text-gray-300'
                        }`}>{prop.data_source === 'idx' ? 'MLS' : 'Manual'}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {prop.listing_url && (
                          <a href={prop.listing_url} target="_blank" rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-[#243B55] text-[#00D4FF] text-sm font-medium rounded-lg hover:bg-[#2d4a6a]">View Listing</a>
                        )}
                        <button onClick={() => handleDelete(prop.id, prop.address)}
                          className="px-3 py-1.5 bg-[#243B55] text-red-400 text-sm font-medium rounded-lg hover:bg-[#2d4a6a]">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#546E7A]/30">
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#B0BEC5]">Address</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#B0BEC5]">City/State</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-[#B0BEC5]">Price</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Bed/Bath</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-[#B0BEC5]">Sqft</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Source</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Status</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProperties.map(prop => (
                        <tr key={prop.id} className="border-b border-[#546E7A]/10 hover:bg-[#243B55]/30 transition-colors">
                          <td className="px-4 py-3">
                            <a href={`/properties/${prop.id}`} className="text-white font-medium text-sm hover:text-[#00D4FF]">{prop.address}</a>
                            {prop.mls_number && <div className="text-gray-500 text-xs">MLS: {prop.mls_number}</div>}
                            {propertyState(prop.id) && (
                              <div className="mt-1">
                                <ScoringChip state={propertyState(prop.id)} failedJob={failedJob('property', prop.id)} onRetry={retryScoring} />
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-300 text-sm">{prop.city}, {prop.state}</td>
                          <td className="px-4 py-3 text-[#00E676] font-medium text-sm text-right">{formatPrice(prop.price)}</td>
                          <td className="px-4 py-3 text-gray-300 text-sm text-center">{prop.beds || '-'}/{prop.baths || '-'}</td>
                          <td className="px-4 py-3 text-gray-300 text-sm text-right">{prop.sqft ? prop.sqft.toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              prop.data_source === 'idx' ? 'bg-cyan-900/50 text-cyan-300' : 'bg-gray-700 text-gray-300'
                            }`}>
                              {prop.data_source === 'idx' ? 'MLS' : 'Manual'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              prop.listing_status === 'active' ? 'bg-green-900/50 text-green-300' :
                              prop.listing_status === 'pending' ? 'bg-yellow-900/50 text-yellow-300' :
                              'bg-red-900/50 text-red-300'
                            }`}>
                              {prop.listing_status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex gap-2 justify-center">
                              {prop.listing_url && (
                                <a href={prop.listing_url} target="_blank" rel="noopener noreferrer"
                                  className="text-[#00D4FF] hover:text-[#00E676] text-sm" title="View listing">👁</a>
                              )}
                              <button onClick={() => handleDelete(prop.id, prop.address)}
                                className="text-gray-600 hover:text-red-400 text-sm" title="Delete">✕</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </div>
              </>
            )}
          </>
        )}

        {/* TAB: Import from MLS */}
        {activeTab === 'idx' && (
          <div>
            {/* IDX Status */}
            {idxStatus && !idxStatus.credentials_configured && (
              <div className="bg-yellow-900/30 border border-yellow-600/30 text-yellow-200 px-4 py-3 rounded-lg mb-4 text-sm">
                SimplyRETS credentials not configured. Contact your administrator to enable MLS integration.
              </div>
            )}

            {/* Buyer count info */}
            {buyers.length > 0 && (
              <div className="bg-[#1A2744] rounded-xl border border-[#00E676]/20 p-4 mb-6">
                <p className="text-[#00E676] text-sm font-medium">
                  Auto-scoring enabled: Imported listings will be automatically scored for your {buyers.length} active buyer{buyers.length !== 1 ? 's' : ''}.
                </p>
              </div>
            )}

            {/* Search Form */}
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6 mb-6">
              <h2 className="text-lg font-semibold text-white mb-4">Search MLS Listings</h2>
              <form onSubmit={handleIdxSearch} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-300 mb-1">Address or keyword</label>
                  <input type="text" value={idxQuery.q} onChange={(e) => setIdxQuery({ ...idxQuery, q: e.target.value })}
                    className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="Search by address, street name, or keyword..." />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">City</label>
                  <input type="text" value={idxQuery.city} onChange={(e) => setIdxQuery({ ...idxQuery, city: e.target.value })}
                    className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="Madison, Huntsville, Melbourne..." />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Min Price</label>
                  <input type="number" value={idxQuery.min_price} onChange={(e) => setIdxQuery({ ...idxQuery, min_price: e.target.value })}
                    className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="250000" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Max Price</label>
                  <input type="number" value={idxQuery.max_price} onChange={(e) => setIdxQuery({ ...idxQuery, max_price: e.target.value })}
                    className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="450000" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Min Beds</label>
                  <input type="number" value={idxQuery.min_beds} onChange={(e) => setIdxQuery({ ...idxQuery, min_beds: e.target.value })}
                    className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="3" />
                </div>
                <div className="flex items-end">
                  <button type="submit" disabled={idxSearching}
                    className="w-full px-6 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] disabled:opacity-50 transition-colors">
                    {idxSearching ? 'Searching MLS...' : 'Search MLS'}
                  </button>
                </div>
              </form>
            </div>

            {/* IDX Results */}
            {idxResults.length > 0 && (
              <div className="space-y-3">
                <p className="text-gray-400 text-sm">{idxResults.length} listings found</p>
                {idxResults.map((listing, i) => (
                  <div key={listing.simplyrets_id || i} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-5">
                    <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-4">
                      {/* Photo */}
                      {listing.photo_url && (
                        <img src={listing.photo_url} alt="" className="w-full md:w-32 h-40 md:h-24 object-cover rounded-lg md:shrink-0" />
                      )}

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-semibold break-words">{listing.address}</div>
                        <div className="text-gray-400 text-sm break-words">{listing.city}, {listing.state} {listing.zip}</div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-400">
                          <span>{listing.beds || '-'} bed</span>
                          <span>{listing.baths || '-'} bath</span>
                          <span>{listing.sqft ? listing.sqft.toLocaleString() : '-'} sqft</span>
                          {listing.lot_size_acres && <span>{listing.lot_size_acres} acres</span>}
                          {listing.year_built && <span>Built {listing.year_built}</span>}
                        </div>
                        {listing.raw_remarks && (
                          <p className="text-gray-500 text-xs mt-2 line-clamp-2">{listing.raw_remarks}</p>
                        )}
                      </div>

                      {/* Price & Import */}
                      <div className="text-left md:text-right md:shrink-0">
                        <div className="text-[#00E676] font-bold text-xl">{formatPrice(listing.price)}</div>
                        {listing.sqft && listing.price && (
                          <div className="text-gray-500 text-sm">${Math.round(listing.price / listing.sqft)}/sqft</div>
                        )}
                        <div className="mt-3">
                          {listing.already_imported ? (
                            <span className="px-3 py-1.5 bg-gray-700 text-gray-400 rounded-lg text-sm">Already Imported</span>
                          ) : (
                            <button
                              onClick={() => handleImport(listing.simplyrets_id, listing.address)}
                              disabled={importing[listing.simplyrets_id]}
                              className="px-4 py-1.5 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 transition-colors text-sm"
                            >
                              {importing[listing.simplyrets_id] ? 'Importing...' : 'Import & Score'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB: Manual Entry */}
        {activeTab === 'manual' && (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
            <h2 className="text-lg font-semibold text-white mb-2">Add Property Manually</h2>
            <p className="text-gray-400 text-sm mb-4">For off-market, FSBO, or properties not in the MLS.</p>
            <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-300 mb-1">Address *</label>
                <input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('address')}`}
                  placeholder="812 Cedar Lane" required />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">MLS #</label>
                <input type="text" value={form.mls_number} onChange={(e) => setForm({ ...form, mls_number: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('mls_number')}`}
                  placeholder="MLS12345" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">City</label>
                <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('city')}`}
                  placeholder="Madison" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">State</label>
                <input type="text" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('state')}`}
                  placeholder="AL" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">ZIP</label>
                <input type="text" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('zip')}`}
                  placeholder="35758" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Price</label>
                <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('price')}`}
                  placeholder="387000" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Beds</label>
                <input type="number" value={form.beds} onChange={(e) => setForm({ ...form, beds: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('beds')}`}
                  placeholder="4" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Baths</label>
                <input type="number" step="0.5" value={form.baths} onChange={(e) => setForm({ ...form, baths: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('baths')}`}
                  placeholder="2.5" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Sqft</label>
                <input type="number" value={form.sqft} onChange={(e) => setForm({ ...form, sqft: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('sqft')}`}
                  placeholder="2800" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Lot Size (acres)</label>
                <input type="number" step="0.01" value={form.lot_size_acres} onChange={(e) => setForm({ ...form, lot_size_acres: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('lot_size_acres')}`}
                  placeholder="0.33" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Year Built</label>
                <input type="number" value={form.year_built} onChange={(e) => setForm({ ...form, year_built: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('year_built')}`}
                  placeholder="1998" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">HOA ($/month)</label>
                <input type="number" value={form.hoa_monthly} onChange={(e) => setForm({ ...form, hoa_monthly: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('hoa_monthly')}`}
                  placeholder="175" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">School District</label>
                <input type="text" value={form.school_district} onChange={(e) => setForm({ ...form, school_district: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('school_district')}`}
                  placeholder="James Clemens" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">School Rating (1-10)</label>
                <input type="number" step="0.1" value={form.school_rating} onChange={(e) => setForm({ ...form, school_rating: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('school_rating')}`}
                  placeholder="9" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Garage Spaces</label>
                <input type="number" value={form.garage_spaces} onChange={(e) => setForm({ ...form, garage_spaces: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('garage_spaces')}`}
                  placeholder="2" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Property Type</label>
                <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('property_type')}`}>
                  <option value="single_family">Single Family</option>
                  <option value="condo">Condo</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="multi_family">Multi-Family</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Listing URL</label>
                <input type="url" value={form.listing_url} onChange={(e) => setForm({ ...form, listing_url: e.target.value })}
                  className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('listing_url')}`}
                  placeholder="https://mls.com/listing/12345" />
              </div>

              {/* ── COLLAPSIBLE SECTIONS for additional MLS fields ── */}
              <div className="md:col-span-3 border-t border-[#546E7A]/20 mt-2 pt-3">
                <p className="text-gray-500 text-xs">Optional details — expand only what applies.</p>
              </div>

              {/* Property Features */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('features')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Property Features</span>
                  <span className={`transition-transform duration-200 ${expandedSections.features ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.features && (
                <>
                  <div>
                    <TriStateSelect label="Pool" value={form.pool}
                      onChange={(v) => setForm({ ...form, pool: v })} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-300 mb-1">Pool Features</label>
                    <input type="text" value={form.pool_features}
                      onChange={(e) => setForm({ ...form, pool_features: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('pool_features')}`}
                      placeholder="Screened, heated, in-ground, saltwater" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Full Baths</label>
                    <input type="number" min="0" value={form.bathrooms_full}
                      onChange={(e) => setForm({ ...form, bathrooms_full: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('bathrooms_full')}`}
                      placeholder="2" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Half Baths</label>
                    <input type="number" min="0" value={form.bathrooms_half}
                      onChange={(e) => setForm({ ...form, bathrooms_half: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('bathrooms_half')}`}
                      placeholder="1" />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm text-gray-300 mb-1">Appliances Included</label>
                    <textarea value={form.appliances}
                      onChange={(e) => setForm({ ...form, appliances: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF] resize-none${autoCls('appliances')}`}
                      rows={2}
                      placeholder="Refrigerator, range, dishwasher, washer, dryer" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Stories</label>
                    <input type="number" min="1" value={form.stories}
                      onChange={(e) => setForm({ ...form, stories: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('stories')}`}
                      placeholder="1" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Furnished</label>
                    <select value={form.furnished} onChange={(e) => setForm({ ...form, furnished: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('furnished')}`}>
                      <option value="">—</option>
                      <option value="Unfurnished">Unfurnished</option>
                      <option value="Partially Furnished">Partially Furnished</option>
                      <option value="Furnished">Furnished</option>
                    </select>
                  </div>
                </>
              )}

              {/* Location */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('location')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Location & Schools</span>
                  <span className={`transition-transform duration-200 ${expandedSections.location ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.location && (
                <>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Elementary School</label>
                    <input type="text" value={form.elementary_school}
                      onChange={(e) => setForm({ ...form, elementary_school: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('elementary_school')}`}
                      placeholder="James Madison Elementary" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Middle School</label>
                    <input type="text" value={form.middle_school}
                      onChange={(e) => setForm({ ...form, middle_school: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('middle_school')}`}
                      placeholder="" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">High School</label>
                    <input type="text" value={form.high_school}
                      onChange={(e) => setForm({ ...form, high_school: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('high_school')}`}
                      placeholder="" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Subdivision</label>
                    <input type="text" value={form.subdivision}
                      onChange={(e) => setForm({ ...form, subdivision: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('subdivision')}`}
                      placeholder="Heritage Plantation" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">County</label>
                    <input type="text" value={form.county}
                      onChange={(e) => setForm({ ...form, county: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('county')}`}
                      placeholder="Brevard" />
                  </div>
                  <div>
                    <TriStateSelect label="Gated Community" value={form.gated_community}
                      onChange={(v) => setForm({ ...form, gated_community: v })} />
                  </div>
                </>
              )}

              {/* Waterfront */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('waterfront')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Waterfront</span>
                  <span className={`transition-transform duration-200 ${expandedSections.waterfront ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.waterfront && (
                <>
                  <div>
                    <TriStateSelect label="Waterfront" value={form.waterfront}
                      onChange={(v) => setForm({ ...form, waterfront: v })} />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm text-gray-300 mb-1">Waterfront Features</label>
                    <input type="text" value={form.waterfront_features}
                      onChange={(e) => setForm({ ...form, waterfront_features: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('waterfront_features')}`}
                      placeholder="Ocean front, seawall, dock, no fixed bridges" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-300 mb-1">Waterfront Type</label>
                    <select value={form.waterfront_type}
                      onChange={(e) => setForm({ ...form, waterfront_type: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('waterfront_type')}`}>
                      <option value="">None</option>
                      <option value="Lake Front">Lake Front</option>
                      <option value="Ocean Front">Ocean Front</option>
                      <option value="River Front">River Front</option>
                      <option value="Canal">Canal</option>
                      <option value="Pond">Pond</option>
                    </select>
                  </div>
                </>
              )}

              {/* Lifestyle */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('lifestyle')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Lifestyle & Community</span>
                  <span className={`transition-transform duration-200 ${expandedSections.lifestyle ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.lifestyle && (
                <>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Pets Allowed</label>
                    <select value={form.pets_allowed}
                      onChange={(e) => setForm({ ...form, pets_allowed: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('pets_allowed')}`}>
                      <option value="">—</option>
                      <option value="Dogs OK">Dogs OK</option>
                      <option value="Cats OK">Cats OK</option>
                      <option value="Both">Both</option>
                      <option value="No Pets">No Pets</option>
                      <option value="Unknown">Unknown</option>
                    </select>
                  </div>
                  <div>
                    <TriStateSelect label="Senior Community (55+)" value={form.senior_community}
                      onChange={(v) => setForm({ ...form, senior_community: v })} />
                  </div>
                  <div>
                    <TriStateSelect label="Accessibility Features" value={form.accessibility_features}
                      onChange={(v) => setForm({ ...form, accessibility_features: v })} />
                  </div>
                  <div>
                    <TriStateSelect label="Dogs Allowed" value={form.dogs_allowed}
                      onChange={(v) => setForm({ ...form, dogs_allowed: v })} />
                  </div>
                  <div>
                    <TriStateSelect label="Cats Allowed" value={form.cats_allowed}
                      onChange={(v) => setForm({ ...form, cats_allowed: v })} />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Direction Faces</label>
                    <select value={form.direction_faces}
                      onChange={(e) => setForm({ ...form, direction_faces: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('direction_faces')}`}>
                      <option value="">—</option>
                      <option value="N">N</option>
                      <option value="S">S</option>
                      <option value="E">E</option>
                      <option value="W">W</option>
                      <option value="NE">NE</option>
                      <option value="NW">NW</option>
                      <option value="SE">SE</option>
                      <option value="SW">SW</option>
                    </select>
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm text-gray-300 mb-1">Community Amenities</label>
                    <textarea value={form.community_amenities}
                      onChange={(e) => setForm({ ...form, community_amenities: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF] resize-none${autoCls('community_amenities')}`}
                      rows={2}
                      placeholder="Pool, gym, tennis, walking trails, clubhouse" />
                  </div>
                </>
              )}

              {/* Utilities */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('utilities')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Utilities & Appliances</span>
                  <span className={`transition-transform duration-200 ${expandedSections.utilities ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.utilities && (
                <>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Cooling</label>
                    <input type="text" value={form.cooling}
                      onChange={(e) => setForm({ ...form, cooling: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('cooling')}`}
                      placeholder="Central Air" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Heating</label>
                    <input type="text" value={form.heating}
                      onChange={(e) => setForm({ ...form, heating: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('heating')}`}
                      placeholder="Gas Forced Air" />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm text-gray-300 mb-1">Appliances</label>
                    <textarea value={form.appliances}
                      onChange={(e) => setForm({ ...form, appliances: e.target.value })}
                      className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF] resize-none${autoCls('appliances')}`}
                      rows={2}
                      placeholder="Range, refrigerator, dishwasher, microwave, washer/dryer" />
                  </div>
                </>
              )}

              {/* Pricing */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('pricing')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Pricing History</span>
                  <span className={`transition-transform duration-200 ${expandedSections.pricing ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.pricing && (
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Original List Price</label>
                  <input type="number" value={form.original_list_price}
                    onChange={(e) => setForm({ ...form, original_list_price: e.target.value })}
                    className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls('original_list_price')}`}
                    placeholder="425000" />
                </div>
              )}

              {/* Description */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('description')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Listing Description</span>
                  <span className={`transition-transform duration-200 ${expandedSections.description ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.description && (
                <div className="md:col-span-3">
                  <textarea value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className={`w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF] resize-y${autoCls('description')}`}
                    rows={5}
                    placeholder="Full listing description, marketing copy, special features…" />
                </div>
              )}

              <div className="md:col-span-3 flex flex-wrap gap-3">
                <button type="submit" disabled={creating}
                  className="px-6 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 transition-colors">
                  {creating ? 'Adding...' : 'Add Property'}
                </button>
                <button type="button" disabled={creating} onClick={(e) => handleCreate(e, true)}
                  className="px-6 py-2 bg-[#1A2744] text-[#00D4FF] font-semibold rounded-lg border border-[#00D4FF]/40 hover:bg-[#243B55] disabled:opacity-50 transition-colors"
                  title="Save privately as a pre-listing draft and open Listing Lab. Buyers never see drafts.">
                  🧪 {creating ? 'Saving…' : 'Save as Listing Lab draft'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* TAB: Upload MLS PDF */}
        {activeTab === 'pdf' && (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
            <h2 className="text-lg font-semibold text-white mb-2">Upload an MLS Listing PDF</h2>
            <p className="text-gray-400 text-sm mb-5">
              Drop in an MLS listing PDF and Claude will extract all the property fields automatically.
              You'll get a chance to review and edit before saving.
            </p>

            {pdfError && (
              <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg text-sm mb-4 flex items-center justify-between flex-wrap gap-2">
                <span>{pdfError}</span>
                <button onClick={() => setPdfError('')} className="text-red-200 underline text-xs hover:text-white">Dismiss</button>
              </div>
            )}

            {!pdfUploading && (
              <div
                onDragOver={(e) => { e.preventDefault(); setPdfDragOver(true); }}
                onDragLeave={() => setPdfDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setPdfDragOver(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) handlePdfFile(f);
                }}
                className={`border-2 border-dashed rounded-xl p-8 md:p-12 text-center transition-colors ${
                  pdfDragOver
                    ? 'border-[#00D4FF] bg-[#00D4FF]/5'
                    : pdfFile
                      ? 'border-[#00E676]/50 bg-[#00E676]/5'
                      : 'border-[#546E7A]/40 bg-[#0A0E27]/30 hover:border-[#546E7A]'
                }`}
              >
                {pdfFile ? (
                  <div>
                    <div className="text-4xl mb-2">📄</div>
                    <div className="text-white font-medium text-sm break-all">{pdfFile.name}</div>
                    <div className="text-gray-500 text-xs mt-1">{(pdfFile.size / 1024).toFixed(0)} KB</div>
                    <div className="flex items-center justify-center gap-3 mt-4 flex-wrap">
                      <button
                        onClick={handlePdfUpload}
                        className="px-5 py-2 bg-gradient-to-r from-[#00D4FF] to-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:opacity-90 transition-opacity"
                      >
                        ✨ Extract with AI
                      </button>
                      <button
                        onClick={() => setPdfFile(null)}
                        className="px-4 py-2 text-gray-400 hover:text-white text-sm transition-colors"
                      >
                        Choose a different file
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-5xl mb-3">📄</div>
                    <div className="text-white font-medium mb-1">Drop your MLS PDF here</div>
                    <div className="text-gray-500 text-sm mb-4">or click below to choose a file</div>
                    <label className="inline-block px-5 py-2 bg-[#243B55] text-[#00D4FF] font-medium rounded-lg hover:bg-[#2d4a6a] cursor-pointer transition-colors">
                      Choose PDF file
                      <input
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        onChange={(e) => handlePdfFile(e.target.files?.[0])}
                      />
                    </label>
                    <div className="text-gray-600 text-xs mt-3">Max 10MB. Text-based PDFs only (scanned images aren't supported yet).</div>
                  </div>
                )}
              </div>
            )}

            {pdfUploading && (
              <div className="border-2 border-[#00D4FF]/40 rounded-xl p-8 md:p-12 text-center bg-[#00D4FF]/5">
                <div className="text-4xl mb-3 animate-pulse">✨</div>
                <div className="text-[#00D4FF] font-medium mb-1">AI is reading your MLS listing...</div>
                <div className="text-gray-500 text-sm">Extracting address, price, beds, baths, and 30+ other fields. This usually takes a few seconds.</div>
              </div>
            )}

            <div className="mt-6 text-gray-500 text-xs">
              <p className="mb-1"><span className="text-gray-400">How this works:</span></p>
              <ol className="list-decimal list-inside space-y-1 text-gray-500">
                <li>Upload a PDF — typically an MLS export, listing flyer, or "agent full" report.</li>
                <li>Claude reads the text and extracts ~35 standard property fields.</li>
                <li>The Manual Entry tab opens with everything pre-filled. Cyan-tinted fields were auto-populated.</li>
                <li>Review, fix anything wrong, and click Add Property to save.</li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
